import { tourFunctions } from "../tour-functions";
const { getIndex } = tourFunctions.currentScene;
const getCurrentSceneIndex = getIndex;

function idleYield() {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => resolve(), { timeout: 2000 });
    } else {
      setTimeout(resolve, 100);
    }
  });
}

function resolveTourAssetUrl(url) {
  if (!url || typeof url !== "string") return null;
  return url.replace(/%SWFPATH%/g, ".");
}

const HOTSPOT_STYLE_ICON_DEFAULTS = {
  hotspot_info: "%SWFPATH%/images/info.svg",
  hotspot_info_custom: "%SWFPATH%/images/info.svg",
  hotspot_gallery: "%SWFPATH%/images/gallery.svg",
  hotspot_video: "%SWFPATH%/images/video.svg",
  hotspot_social: "%SWFPATH%/images/social-instagram-reel.svg",
  hotspot_accessibility: "%SWFPATH%/images/accessibility/accessibility-2.svg",
  infosidebar: "%SWFPATH%/images/info.svg",
};

const TOUR_HOTSPOT_ICON_KEYS = {
  hotspot_info: "hotspotInfo",
  hotspot_info_custom: "hotspotInfo",
  hotspot_gallery: "hotspotGallery",
  hotspot_video: "hotspotVideo",
  hotspot_social: "hotspotSocial",
};

const LINKED_SCENE_HOTSPOT_STYLES = new Set([
  "skin_hotspotstyle",
  "skin_hotspotstyle_balloon",
]);

/** Shared across Preloader instances (Loader + Tour) to avoid duplicate fetches. */
const globalLoadedUrls = new Set();
const globalInFlightUrls = new Map();

/** Blocks background preloads until the loader finishes the start scene. */
const firstSceneGate = {
  complete: false,
  /** @type {Promise<void> | null} */
  promise: null,
  /** @type {(() => void) | null} */
  resolve: null,
};

function beginFirstScenePreload() {
  firstSceneGate.complete = false;
  firstSceneGate.promise = new Promise((resolve) => {
    firstSceneGate.resolve = resolve;
  });
}

function completeFirstScenePreload() {
  firstSceneGate.complete = true;
  firstSceneGate.resolve?.();
  firstSceneGate.resolve = null;
}

async function waitForFirstScenePreload() {
  if (firstSceneGate.complete) return;
  if (firstSceneGate.promise) await firstSceneGate.promise;
}

// Preloader.js
export class Preloader {
  constructor(tour, directions = ["u", "d", "f", "b", "l", "r"]) {
    this.tour = tour;
    this.directions = directions;
    this.preloadPromises = [];
    this.scenes = tour.scenes?.filter((scene) => !scene.hidden);
    this.isStopped = false;
    this.loadedUrls = new Set();
    this.inFlightUrls = new Map();
    this.preloadedScenePanoIndices = new Set();
  }

  stop() {
    const wasStopped = this.isStopped;
    this.preloadPromises = [];
    this.isStopped = true;
    if (!wasStopped) {
      console.log("[Preloader] stop() — preload cancelled");
    }
  }

  clampSceneIndex(sceneIndex) {
    if (!this.scenes?.length) return 0;
    return Math.max(0, Math.min(sceneIndex, this.scenes.length - 1));
  }

  preloadImage(url) {
    if (this.isStopped || !url) return Promise.resolve();
    if (globalLoadedUrls.has(url) || this.loadedUrls.has(url)) {
      this.loadedUrls.add(url);
      return Promise.resolve();
    }
    if (globalInFlightUrls.has(url)) return globalInFlightUrls.get(url);
    if (this.inFlightUrls.has(url)) return this.inFlightUrls.get(url);

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        globalLoadedUrls.add(url);
        this.loadedUrls.add(url);
        globalInFlightUrls.delete(url);
        this.inFlightUrls.delete(url);
        resolve();
      };
      img.onerror = (err) => {
        globalInFlightUrls.delete(url);
        this.inFlightUrls.delete(url);
        reject(err);
      };
    });
    globalInFlightUrls.set(url, promise);
    this.inFlightUrls.set(url, promise);
    return promise;
  }

  async preloadImages(urls, { sequential = false } = {}) {
    if (this.isStopped) return;
    const pending = [...new Set(urls)].filter(
      (url) =>
        url && !this.loadedUrls.has(url) && !globalLoadedUrls.has(url),
    );
    if (!pending.length) return;

    if (sequential) {
      for (const url of pending) {
        if (this.isStopped) return;
        await this.preloadImage(url);
      }
      return;
    }

    this.preloadPromises = pending.map((url) => this.preloadImage(url));

    try {
      await Promise.all(this.preloadPromises);
    } catch (error) {
      console.error("Error preloading images", error);
    }
  }

  getScenePreviewUrl(scene) {
    return scene.previewUrl ? [scene.previewUrl] : [];
  }

  getSceneThumbUrl(scene) {
    return scene.thumbUrl ? [scene.thumbUrl] : [];
  }

  getSceneEssentialUrls(scene) {
    return [...this.getScenePreviewUrl(scene), ...this.getSceneThumbUrl(scene)];
  }

  getScenePanoUrls(scene) {
    const urls = [];
    scene.images?.forEach((image) => {
      this.directions.forEach((direction) => {
        urls.push(image.url.replace("%s", direction));
      });
    });
    return urls;
  }

  getSceneImageUrls(scene) {
    return [
      ...this.getSceneEssentialUrls(scene),
      ...this.getScenePanoUrls(scene),
    ];
  }

  async preloadScenePreview(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    const scene = this.scenes[index];
    if (!scene) return;

    const urls = this.getScenePreviewUrl(scene);
    if (!urls.length) return;

    const t0 = performance.now();
    console.log("[Preloader] preloadScenePreview start", {
      sceneIndex: index,
      urlCount: urls.length,
    });

    await this.preloadImages(urls);

    console.log("[Preloader] preloadScenePreview done", {
      sceneIndex: index,
      elapsedMs: Math.round(performance.now() - t0),
    });
  }

  async preloadSceneThumb(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    const scene = this.scenes[index];
    if (!scene) return;

    const urls = this.getSceneThumbUrl(scene);
    if (!urls.length) return;

    await this.preloadImages(urls);
  }

  async preloadSceneEssentials(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    if (!this.scenes[index]) return;

    const t0 = performance.now();
    const urls = this.getSceneEssentialUrls(this.scenes[index]);
    console.log("[Preloader] preloadSceneEssentials start", {
      sceneIndex: index,
      urlCount: urls.length,
    });

    await this.preloadImages(urls);

    console.log("[Preloader] preloadSceneEssentials done", {
      sceneIndex: index,
      elapsedMs: Math.round(performance.now() - t0),
    });
  }

  async preloadScenePano(sceneIndex, { sequential = false } = {}) {
    const index = this.clampSceneIndex(sceneIndex);
    if (!this.scenes[index] || this.preloadedScenePanoIndices.has(index)) {
      return;
    }

    const urls = this.getScenePanoUrls(this.scenes[index]);
    await this.preloadImages(urls, { sequential });
    this.preloadedScenePanoIndices.add(index);
  }

  async preloadScene(sceneIndex) {
    if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
      console.error("Invalid scene index");
      return;
    }

    await this.preloadScenePreview(sceneIndex);
    if (this.isStopped) return;
    await this.preloadScenePano(sceneIndex);
    if (this.isStopped) return;
    await this.preloadSceneThumb(sceneIndex);
  }

  async preloadFirstScene() {
    await this.preloadScene(0);
  }

  /**
   * Preload the scene at the given index (e.g. start scene from URL hash/query).
   */
  async preloadStartScene(sceneIndex) {
    await this.preloadScene(sceneIndex);
  }

  async preloadNextScene() {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    if (this.scenes.length <= 1) return;
    const nextSceneIndex = (getCurrentSceneIndex() + 1) % this.scenes.length;
    await this.preloadScenePano(nextSceneIndex);
  }

  async preloadPreviousScene() {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    if (this.scenes.length <= 1) return;
    const prevSceneIndex =
      (getCurrentSceneIndex() - 1 + this.scenes.length) % this.scenes.length;
    await this.preloadScenePano(prevSceneIndex);
  }

  async preloadAdjacentScenes() {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    if (this.scenes.length <= 1) return;
    await this.preloadNextScene();
    if (this.isStopped) return;
    await this.preloadPreviousScene();
  }

  findSceneByName(sceneName) {
    if (!sceneName) return null;
    return this.scenes.find((scene) => scene.name === sceneName) ?? null;
  }

  getHotspotStyleIconUrl(hotspot) {
    const defaultPath = HOTSPOT_STYLE_ICON_DEFAULTS[hotspot.style];
    if (!defaultPath) return null;

    const tourKey = TOUR_HOTSPOT_ICON_KEYS[hotspot.style];
    const tourUrl = tourKey ? this.tour?.[tourKey]?.url : null;
    return resolveTourAssetUrl(tourUrl ?? defaultPath);
  }

  getCustomPresetIconUrls(hotspot) {
    if (
      hotspot.style !== "hotspot_info" &&
      hotspot.style !== "hotspot_info_custom"
    ) {
      return [];
    }

    const presetId = hotspot.customPresetImage?.trim();
    if (!presetId) return [];

    const colonIndex = presetId.indexOf(":");
    if (colonIndex <= 0) return [];

    const prefix = presetId.slice(0, colonIndex);
    const iconName = presetId.slice(colonIndex + 1);
    if (!prefix || !iconName) return [];

    const fillHex =
      hotspot.style === "hotspot_info_custom"
        ? hotspot.fillColor?.trim() || "#ffffff"
        : this.tour?.theme?.hotspotColor?.trim() ||
          this.tour?.theme?.primaryColor ||
          "#000000";
    const hex = fillHex.replace("#", "");
    const urls = [
      `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(iconName)}.svg?color=%23${encodeURIComponent(hex)}`,
    ];

    if (hotspot.style === "hotspot_info_custom" && hotspot.hoverFillColor?.trim()) {
      const hoverHex = hotspot.hoverFillColor.replace("#", "").trim();
      if (hoverHex) {
        urls.push(
          `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(iconName)}.svg?color=%23${encodeURIComponent(hoverHex)}`,
        );
      }
    }

    return urls;
  }

  getHotspotAssetUrls(hotspot) {
    const urls = [];

    if (hotspot.popupImage?.url) {
      urls.push(resolveTourAssetUrl(hotspot.popupImage.url));
    }

    hotspot.galleryImages?.forEach((image) => {
      if (image?.url) urls.push(resolveTourAssetUrl(image.url));
    });

    if (hotspot.popupSocialContent?.thumbnailAsset?.url) {
      urls.push(
        resolveTourAssetUrl(hotspot.popupSocialContent.thumbnailAsset.url),
      );
    }

    if (
      hotspot.linkedScene &&
      LINKED_SCENE_HOTSPOT_STYLES.has(hotspot.style)
    ) {
      const linkedScene = this.findSceneByName(hotspot.linkedScene);
      if (linkedScene?.thumbUrl) {
        urls.push(resolveTourAssetUrl(linkedScene.thumbUrl));
      }
    }

    const styleIconUrl = this.getHotspotStyleIconUrl(hotspot);
    if (styleIconUrl) urls.push(styleIconUrl);

    urls.push(...this.getCustomPresetIconUrls(hotspot));

    return urls.filter(Boolean);
  }

  getSceneHotspotAssetUrls(scene) {
    const urls = [];
    if (scene.logo?.url) {
      urls.push(resolveTourAssetUrl(scene.logo.url));
    }

    const hotspots = scene.hotspots?.filter((hotspot) => !hotspot.hidden) ?? [];
    for (const hotspot of hotspots) {
      urls.push(...this.getHotspotAssetUrls(hotspot));
    }

    return urls;
  }

  async preloadSceneHotspotAssets(sceneIndex) {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    const index = this.clampSceneIndex(sceneIndex);
    const scene = this.scenes[index];
    if (!scene) return;

    const t0 = performance.now();
    const urls = this.getSceneHotspotAssetUrls(scene);
    console.log("[Preloader] preloadSceneHotspotAssets start", {
      sceneIndex: index,
      urlCount: urls.length,
    });

    await this.preloadImages(urls);

    console.log("[Preloader] preloadSceneHotspotAssets done", {
      sceneIndex: index,
      elapsedMs: Math.round(performance.now() - t0),
    });
  }

  /**
   * Loader gate: preview, thumb, then pano faces (sequential).
   * Nothing else preloads until this completes.
   */
  async preloadStartSceneForLoader(sceneIndex) {
    const index = this.clampSceneIndex(sceneIndex);
    const t0 = performance.now();
    beginFirstScenePreload();
    console.log("[Preloader] preloadStartSceneForLoader start", {
      sceneIndex: index,
    });

    try {
      await this.preloadScenePreview(index);
      if (this.isStopped) return;
      await this.preloadSceneThumb(index);
      if (this.isStopped) return;
      await this.preloadScenePano(index, { sequential: true });

      console.log("[Preloader] preloadStartSceneForLoader done", {
        sceneIndex: index,
        elapsedMs: Math.round(performance.now() - t0),
      });
    } finally {
      completeFirstScenePreload();
    }
  }

  getHotspotImageUrls(hotspot) {
    return this.getHotspotAssetUrls(hotspot);
  }

  async preloadHotspot(hotspot) {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    const urls = this.getHotspotImageUrls(hotspot);
    await this.preloadImages(urls);
  }

  async preloadHotspots(hotspots) {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    const allUrls = hotspots.flatMap((hotspot) =>
      this.getHotspotImageUrls(hotspot),
    );
    await this.preloadImages(allUrls);
  }

  async preloadCurrentScene() {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    const index = getCurrentSceneIndex();
    if (index < 0 || index >= this.scenes.length) return;
    await this.preloadSceneHotspotAssets(index);
  }

  async preloadThumbnails() {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    for (const scene of this.scenes) {
      if (this.isStopped) return;
      if (!scene.thumbUrl) continue;
      await this.preloadImage(scene.thumbUrl);
    }
  }

  /**
   * Loader-time preload: start scene preview + thumb only.
   * @param {number} [startSceneIndex=0]
   */
  async initialPreload(startSceneIndex = 0) {
    await this.preloadSceneEssentials(startSceneIndex);
  }

  /**
   * Preload full pano assets for every scene except the start index.
   * Runs sequentially to limit network contention. No-op if stopped.
   */
  async preloadRemainingScenes(startSceneIndex = 0) {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    if (!this.scenes?.length) return;

    const index = this.clampSceneIndex(startSceneIndex);
    const t0 = performance.now();
    console.log("[Preloader] preloadRemainingScenes start", {
      skipIndex: index,
      sceneCount: this.scenes.length,
    });

    for (let i = 0; i < this.scenes.length; i++) {
      if (this.isStopped) {
        console.log("[Preloader] preloadRemainingScenes cancelled", {
          elapsedMs: Math.round(performance.now() - t0),
        });
        return;
      }
      if (i === index || this.preloadedScenePanoIndices.has(i)) continue;

      const sceneT0 = performance.now();
      await this.preloadScenePano(i);
      console.log("[Preloader] preloadRemainingScenes scene", {
        sceneIndex: i,
        elapsedMs: Math.round(performance.now() - sceneT0),
      });
    }

    console.log("[Preloader] preloadRemainingScenes done", {
      elapsedMs: Math.round(performance.now() - t0),
    });
  }

  /**
   * Low-priority background preload for scenes not yet loaded.
   * Yields between scenes so krpano and UI stay responsive.
   */
  async preloadRemainingScenesIdle(startSceneIndex = 0) {
    await waitForFirstScenePreload();
    if (this.isStopped) return;
    if (!this.scenes?.length) return;

    const index = this.clampSceneIndex(startSceneIndex);
    const t0 = performance.now();
    console.log("[Preloader] preloadRemainingScenesIdle start", {
      skipIndex: index,
      sceneCount: this.scenes.length,
    });

    for (let i = 0; i < this.scenes.length; i++) {
      if (this.isStopped) {
        console.log("[Preloader] preloadRemainingScenesIdle cancelled", {
          elapsedMs: Math.round(performance.now() - t0),
        });
        return;
      }
      if (i === index || this.preloadedScenePanoIndices.has(i)) continue;

      await idleYield();
      if (this.isStopped) return;

      const sceneT0 = performance.now();
      await this.preloadScenePano(i);
      console.log("[Preloader] preloadRemainingScenesIdle scene", {
        sceneIndex: i,
        elapsedMs: Math.round(performance.now() - sceneT0),
      });
    }

    console.log("[Preloader] preloadRemainingScenesIdle done", {
      elapsedMs: Math.round(performance.now() - t0),
    });
  }
}
